import { useCallback, useLayoutEffect, useRef, useState } from 'react';

interface UseTabStripCapacityOptions {
  /** Number of document tabs competing for space in the strip. */
  itemCount: number;
  /**
   * Freeze capacity while true. The inline rename editor swaps a truncated tab
   * (~80px) for a ~200px input box; measuring then would fold that outlier into
   * the widest-tab tracking and could bump the tab being edited out of view
   * mid-rename. Measurement resumes — and re-runs — once this returns to false.
   */
  freezeMeasurement?: boolean;
  /**
   * Identity of the currently pinned/active tab. Changing it re-runs measurement
   * because the active tab renders a hair wider (border + shadow), which can push
   * a borderline strip just past its width and needs one fewer visible tab.
   */
  activeId?: string | null;
}

// Show every tab until the first real measurement, so the strip doesn't flash a
// collapsed set on mount.
const SHOW_ALL = Number.POSITIVE_INFINITY;

// gap-1 between tabs (0.25rem at the default root font size).
export const TAB_GAP_PX = 4;

/**
 * Marks the "+N more" trigger inside the measured row so width bookkeeping can
 * tell it apart from a real tab: its width is space that comes *back* when the
 * last overflowed tab is reclaimed, and it must never be mistaken for a tab
 * width when estimating whether another tab would fit.
 */
export const OVERFLOW_TRIGGER_ATTR = 'data-tab-overflow-trigger';

export interface CapacityDecisionInput {
  /** Space the strip has to work with (the scroll container's clientWidth). */
  available: number;
  /**
   * TRUE width of the row's contents, including the "+N more" trigger — summed
   * from the children rather than read off `scrollWidth`. See `measure` for why
   * `scrollWidth` cannot be used here.
   */
  content: number;
  /** Capacity currently rendered. Callers resolve SHOW_ALL to itemCount first. */
  current: number;
  itemCount: number;
  /**
   * Assumed width of each tab that would come back, used only when no
   * measurement is on record. The NARROWEST tab seen, deliberately: an
   * optimistic guess that grows too eagerly is corrected on the very next pass
   * by the overflow check, whereas a pessimistic one silently leaves the strip
   * under-filled.
   */
  growthTabWidth: number;
  /** Measured width of the "+N more" trigger, reclaimed when the last tab returns. */
  triggerWidth: number;
  /**
   * capacity → the content width measured the last time that capacity was
   * rendered. This is the hysteresis band, and it is *measured* rather than
   * estimated: growing back to capacity K is safe exactly when the strip is at
   * least as wide as K was last observed to need. Entries are overwritten with
   * each fresh observation rather than kept at a high-water mark, so a tab that
   * gets narrower (a shorter rename, an icon disappearing) releases the space it
   * used to reserve instead of holding it forever.
   *
   * The records are read in both directions. A recorded capacity the strip is
   * now wide enough for is a destination growth can jump straight to; a recorded
   * capacity it is too narrow for is a ceiling growth must stay under, and that
   * ceiling is what keeps an optimistic estimate from bouncing off the same
   * capacity pass after pass.
   */
  requiredWidths: ReadonlyMap<number, number>;
  /**
   * Largest prefix of the currently rendered tabs that fits in `available`,
   * measured from the actual child widths (with "+N more" space reserved while
   * any tab would remain overflowed). The shrink path jumps straight here:
   * stepping down one tab per layout pass meant a strip opening with N
   * documents ran N-minus-fit nested updates, and React throws "Maximum update
   * depth exceeded" past 50 — the first 58-document chart crossed that
   * (imported print renders).
   */
  fittingCapacity: number;
}

/**
 * Decide the next capacity for the strip. Pure, so the arithmetic can be tested
 * without a layout engine (jsdom has none).
 *
 * Shrinking is driven by direct observation — the row is wider than the space it
 * has, so give a tab back. Growing is the direction that shipped broken, for two
 * compounding reasons. It compared free space against the *widest tab ever seen*
 * while ignoring that reclaiming a tab also shrinks or retires the "+N more"
 * trigger; and, more fundamentally, the free space it compared was always zero
 * (see `measure`), so the condition could not fire at all. The strip collapsed
 * correctly and then never spread back out.
 *
 * Growth now takes the measured path first: each time a capacity is rendered we
 * record what it actually needed, and grow back into it once the strip is
 * genuinely that wide. That is exact, and it cannot oscillate — if growth into a
 * recorded capacity still overflows, the shrink re-records that capacity as
 * needing more than is available, which blocks the next attempt. When no record
 * exists (the documents changed) the estimate takes over, deliberately
 * optimistic: an over-eager grow is undone on the next pass, an over-cautious one
 * leaves the strip silently under-filled.
 *
 * Both directions move in jumps, and for the same reason. Stepping one tab per
 * layout pass is one nested React update per tab, and React throws past 50; a
 * strip that opens narrow (a collapsed side panel, a mobile breakpoint) and then
 * widens across 58 documents would chain the same run of updates growing that
 * the 58-document chart chained shrinking. So growth jumps to the widest
 * recorded capacity that fits, and, with no record to jump to, adds as many tabs
 * as the estimate says the free space holds. Over-growth is cheap now: the
 * shrink path lands back on the measured fit in a single pass, and the capacity
 * that overflowed is recorded as a ceiling growth will not reach again.
 */
export function nextTabStripCapacity({
  available,
  content,
  current,
  itemCount,
  growthTabWidth,
  triggerWidth,
  requiredWidths,
  fittingCapacity,
}: CapacityDecisionInput): number {
  // Overflowing: drop directly to the measured fit. Shrinking one tab per
  // layout pass would be N nested updates for an N-document strip, and React
  // caps nested updates at 50 (see `fittingCapacity`). The 1px slack absorbs
  // sub-pixel rounding.
  if (content > available + 1 && current > 1) {
    return Math.max(1, Math.min(current - 1, fittingCapacity));
  }
  if (current >= itemCount) {
    return current;
  }

  // Read the records once, in both directions: the widest capacity above
  // `current` the strip is now known to be wide enough for, and the narrowest
  // one it is known to be too narrow for. The second is a hard ceiling — it is a
  // direct observation that the row did not fit at that capacity in this much
  // space — and it is what makes an optimistic estimate safe to repeat.
  let recordedFit = current;
  let ceiling = itemCount;
  for (const [capacity, needed] of requiredWidths) {
    if (capacity <= current) continue;
    if (needed <= available) {
      recordedFit = Math.max(recordedFit, capacity);
    } else {
      ceiling = Math.min(ceiling, capacity - 1);
    }
  }
  if (ceiling <= current) return current;
  if (recordedFit > current) return Math.min(recordedFit, ceiling);

  // Nothing on record above `current` — the document set changed, so the
  // recorded widths described a row that no longer exists. Assume every
  // returning tab is as narrow as the narrowest seen, and credit the trigger's
  // width when the growth retires the overflow entirely, since that space goes
  // straight back to tabs.
  const step = growthTabWidth + TAB_GAP_PX;
  const free = available - content;
  const remaining = itemCount - current;
  if (
    ceiling === itemCount &&
    free + triggerWidth + TAB_GAP_PX >= remaining * step
  ) {
    return itemCount;
  }
  const grown = Math.floor(free / step);
  if (grown < 1) return current;
  const target = current + grown;
  if (target <= ceiling) return target;

  // The estimate wants past a capacity already measured as too narrow. Sitting
  // on the ceiling instead would walk down one tab per failed pass, which is the
  // run of nested updates this whole rule exists to avoid, so halve the distance
  // and let the next pass halve what is left.
  return current + Math.ceil((ceiling - current) / 2);
}

/**
 * Measure how many document tabs fit in the strip and expose that capacity, so
 * the rest overflow into a dropdown instead of a hidden horizontal scroll.
 *
 * The strip's available width (the scroll container's clientWidth) is compared
 * against the row's content width. Capacity moves in jumps and settles: it drops
 * straight to the measured fit while the row overflows, and grows back to the
 * widest capacity the strip is measured to have room for. Neither direction
 * steps one tab per layout pass — that is a nested React update per tab, and a
 * 58-document strip runs past the 50 React allows.
 *
 * The content width is SUMMED FROM THE CHILDREN, not read from `row.scrollWidth`.
 * The row is a block-level child filling its scroll container, so the CSSOM
 * clamps its `scrollWidth` to at least its own `clientWidth` — i.e. to the
 * container's width. Whenever the row is not overflowing, `scrollWidth` reports
 * exactly the available width and free space measures as zero no matter how empty
 * the strip really is. That silently disabled every width-based growth check in
 * the first cut of this hook; verified in Chromium (368px of tabs in a 600px
 * container reports `scrollWidth === 600`).
 *
 * Widths are bookkept per element kind: the "+N more" trigger is identified by
 * `OVERFLOW_TRIGGER_ATTR` and tracked separately, so it is never mistaken for a
 * tab. The narrowest-tab record drives the optimistic growth estimate, and both
 * records are safe from the ~200px rename editor because measurement is frozen
 * for the duration of a rename. State only changes when the capacity actually
 * changes, so a stable layout stops re-rendering.
 */
export function useTabStripCapacity({
  itemCount,
  freezeMeasurement = false,
  activeId,
}: UseTabStripCapacityOptions) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tabsRowRef = useRef<HTMLDivElement | null>(null);
  const minTabWidthRef = useRef(Number.POSITIVE_INFINITY);
  const triggerWidthRef = useRef(0);
  const requiredWidthsRef = useRef<Map<number, number>>(new Map());
  const lastItemCountRef = useRef(itemCount);
  // Mirrors `capacity` so the measurement pass can read it without doing its
  // width bookkeeping inside a state updater (updaters must stay pure).
  const capacityRef = useRef<number>(SHOW_ALL);
  const [capacity, setCapacity] = useState<number>(SHOW_ALL);

  const measure = useCallback(() => {
    // While a rename is in progress the strip holds a ~200px editor box; leave
    // capacity (and the width records) untouched until it resolves.
    if (freezeMeasurement) return;
    const container = scrollContainerRef.current;
    const row = tabsRowRef.current;
    if (!container || !row) return;

    const available = container.clientWidth;
    // Not laid out yet, or jsdom (which reports 0): leave capacity at SHOW_ALL.
    if (available === 0) return;

    // Adding or removing a document invalidates every recorded width — they
    // describe a row that no longer exists. Dropping them hands growth to the
    // estimate until each capacity is observed again.
    if (lastItemCountRef.current !== itemCount) {
      requiredWidthsRef.current.clear();
      minTabWidthRef.current = Number.POSITIVE_INFINITY;
      lastItemCountRef.current = itemCount;
    }

    // Sum the children rather than trusting row.scrollWidth — see the note above
    // on why the CSSOM makes scrollWidth useless for measuring free space.
    let content = 0;
    const tabWidths: number[] = [];
    for (const child of Array.from(row.children)) {
      const el = child as HTMLElement;
      const width = el.offsetWidth;
      content += width + (content > 0 ? TAB_GAP_PX : 0);
      if (el.hasAttribute(OVERFLOW_TRIGGER_ATTR)) {
        triggerWidthRef.current = width;
      } else {
        tabWidths.push(width);
        if (width > 0) {
          minTabWidthRef.current = Math.min(minTabWidthRef.current, width);
        }
      }
    }

    // Largest prefix of the rendered tabs that fits, reserving "+N more" space
    // while any tab would remain overflowed. The trigger width may still be 0
    // (never rendered); the next pass measures it and corrects in one step.
    let fitting = 0;
    let fittingWidth = 0;
    for (const width of tabWidths) {
      const withTab = fittingWidth + (fitting > 0 ? TAB_GAP_PX : 0) + width;
      const reserve =
        fitting + 1 < itemCount ? triggerWidthRef.current + TAB_GAP_PX : 0;
      if (withTab + reserve > available) break;
      fittingWidth = withTab;
      fitting += 1;
    }

    const current =
      capacityRef.current === SHOW_ALL ? itemCount : capacityRef.current;

    // Record what this capacity needs right now. Overwriting (rather than keeping
    // a high-water mark) is what lets the strip reclaim space when tabs get
    // narrower; the value is an exact observation either way.
    requiredWidthsRef.current.set(current, content);

    const next = nextTabStripCapacity({
      available,
      content,
      current,
      itemCount,
      growthTabWidth: Number.isFinite(minTabWidthRef.current)
        ? minTabWidthRef.current
        : content,
      triggerWidth: triggerWidthRef.current,
      requiredWidths: requiredWidthsRef.current,
      fittingCapacity: fitting,
    });

    if (next === current) {
      // Everything fits and nothing wants to move; stay as-is (including at the
      // initial SHOW_ALL, so a strip that never overflows never re-renders).
      return;
    }
    capacityRef.current = next;
    setCapacity(next);
  }, [itemCount, freezeMeasurement]);

  // Re-measure after mount, after a tab is added/removed, after each capacity
  // step until it settles, when the active/pinned tab changes (it renders a hair
  // wider), and when a rename ends (measure's identity flips with the freeze).
  useLayoutEffect(() => {
    measure();
  }, [measure, capacity, activeId]);

  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => measure());
    observer.observe(container);
    return () => observer.disconnect();
  }, [measure]);

  return { scrollContainerRef, tabsRowRef, capacity };
}
